import { useEffect, useState, useMemo } from "react";
import { Container, Row, Col, Table, Form, Button, Card, Badge, Modal, ButtonGroup } from "react-bootstrap";
import { getSaldosTesoreria, getEmitidosTesoreria, getRecibidosTesoreria } from "../helpers/rutaTesoreria";

const tbl  = { fontSize: "1.0rem", lineHeight: 1.15 };
const cell = { paddingTop: ".35rem", paddingBottom: ".35rem" };
const right = { textAlign: "right" };
const $ = (n) => Number(n||0).toLocaleString("es-AR",{minimumFractionDigits:2, maximumFractionDigits:2});
const pad2 = (n)=> String(n).padStart(2,"0");
const fmt = (d)=> `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;

export default function InformeTesoreria() {
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [data, setData] = useState(null);

  // Emitidos (cheques / iCheq)
  const [showDet, setShowDet] = useState(false);
  const [detTitulo, setDetTitulo] = useState("");
  const [detRows, setDetRows] = useState([]);
  const [detTotal, setDetTotal] = useState(0);

  // Recibidos (cartera)
  const [showDetRec, setShowDetRec] = useState(false);
  const [detRecTitulo, setDetRecTitulo] = useState("");
  const [detRecRows, setDetRecRows] = useState([]);
  const [detRecTotal, setDetRecTotal] = useState(0);

  // ===== Helpers CSV / PDF =====
  const toCSV = (rows, headers, filename = "detalle.csv") => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g,'""')}"`;
    const head = headers.map(h => esc(h.label)).join(",");
    const body = (rows || []).map(r => headers.map(h => esc(h.get(r))).join(",")).join("\n");
    const blob = new Blob([head + "\n" + body], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };
  const dateStr = (d) => (d ? new Date(d).toLocaleDateString() : "");

  const buildPDF = (title, subtitle, headers, rows) => {
    const ths = headers.map(h=>`<th style="padding:6px;border:1px solid #ccc;text-align:${h.align||'left'}">${h.label}</th>`).join("");
    const trs = rows.map(r => {
      const tds = headers.map(h => {
        const val = h.get(r);
        const txt = h.format ? h.format(val) : String(val ?? "");
        const align = h.align || "left";
        return `<td style="padding:6px;border:1px solid #ddd;text-align:${align}">${txt}</td>`;
      }).join("");
      return `<tr>${tds}</tr>`;
    }).join("");
    const html = `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${title}</title>
        </head>
        <body style="font-family: Arial, sans-serif; padding: 16px;">
          <h2 style="margin: 0 0 6px 0;">${title}</h2>
          <div style="color:#666; margin-bottom:12px;">${subtitle || ""}</div>
          <table style="border-collapse: collapse; width: 100%; font-size: 12px;">
            <thead style="background:#f5f5f5;">
              <tr>${ths}</tr>
            </thead>
            <tbody>${trs}</tbody>
          </table>
        </body>
      </html>`;
    const w = window.open("", "_blank");
    w.document.open(); w.document.write(html); w.document.close();
    // permito que el navegador renderice el documento antes de imprimir
    setTimeout(()=> w.print(), 250);
  };

  const exportEmitidosCSV = () => {
    const fname = `emitidos_${new Date().toISOString().slice(0,19).replace(/[:T]/g,"-")}.csv`;
    toCSV(detRows, [
      { label:"Fecha",   get: r => dateStr(r.fecha) },
      { label:"N° OP",   get: r => r.numeroOP || "" },
      { label:"Tercero", get: r => r.tercero || "" },
      { label:"Medio",   get: r => r.medio || "" },
      { label:"Ref/N°",  get: r => r.referencia || r.nroCheque || "" },
      { label:"Importe", get: r => Number(r.importe || 0) }
    ], fname);
  };
  const exportEmitidosPDF = () => {
    buildPDF(
      detTitulo || "Detalle emitidos",
      `Generado: ${new Date().toLocaleString()}`,
      [
        { label:"Fecha", align:"left",  get: r => r.fecha, format: v => dateStr(v) },
        { label:"N° OP", align:"left",  get: r => r.numeroOP },
        { label:"Tercero", align:"left", get: r => r.tercero },
        { label:"Medio", align:"left",  get: r => r.medio },
        { label:"Ref/N°", align:"left", get: r => r.referencia || r.nroCheque || "" },
        { label:"Importe", align:"right", get: r => r.importe, format: v => `$${$(v)}` },
      ],
      detRows
    );
  };

  const exportRecibidosCSV = () => {
    const fname = `recibidos_${new Date().toISOString().slice(0,19).replace(/[:T]/g,"-")}.csv`;
    toCSV(detRecRows, [
      { label:"Fecha recibida", get: r => dateStr(r.recibidoEn) },
      { label:"Cliente",        get: r => (r?.clienteId?.razonsocial || r?.clienteId?.nombre || "") },
      { label:"Tipo",           get: r => r.tipo || "" },
      { label:"Ref/N°",         get: r => r.idICheq || r.nroCheque || "" },
      { label:"Importe",        get: r => Number(r.importe || 0) }
    ], fname);
  };
  const exportRecibidosPDF = () => {
    buildPDF(
      detRecTitulo || "Valores recibidos",
      `Generado: ${new Date().toLocaleString()}`,
      [
        { label:"Fecha recibida", align:"left", get: r => r.recibidoEn, format: v => dateStr(v) },
        { label:"Cliente", align:"left",        get: r => (r?.clienteId?.razonsocial || r?.clienteId?.nombre || "") },
        { label:"Tipo", align:"left",           get: r => r.tipo || "" },
        { label:"Ref/N°", align:"left",         get: r => r.idICheq || r.nroCheque || "" },
        { label:"Importe", align:"right",       get: r => r.importe, format: v => `$${$(v)}` },
      ],
      detRecRows
    );
  };
  // ===========================

  // ===== Presets de fechas =====
  const setPreset = (key) => {
    const today = new Date();
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    let start = new Date(end);

    if (key === "hoy") {
      // start = end
    } else if (key === "semana") {
      const dow = end.getDay(); // 0=Dom
      const delta = (dow + 6) % 7; // Lunes
      start = new Date(end); start.setDate(end.getDate() - delta);
    } else if (key === "mes") {
      start = new Date(end.getFullYear(), end.getMonth(), 1);
    } else if (key === "mes_anterior") {
      const m = end.getMonth() - 1;
      const y = end.getFullYear() + (m < 0 ? -1 : 0);
      const realM = (m + 12) % 12;
      start = new Date(y, realM, 1);
      const lastDay = new Date(y, realM + 1, 0);
      setHasta(fmt(lastDay));
      setDesde(fmt(start));
      return;
    } else if (key === "anio") {
      start = new Date(end.getFullYear(), 0, 1);
    }

    setDesde(fmt(start));
    setHasta(fmt(end));
  };
  // =============================

  const cargar = async () => {
    const params = {};
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    const resp = await getSaldosTesoreria(params);
    if (resp?.ok) setData(resp);
    else alert(resp?.msg || "No se pudo obtener tesorería.");
  };
  useEffect(() => { cargar(); }, []); // eslint-disable-line

  // Totales
  const totalCajas   = useMemo(()=> data?.totales?.efectivo || 0, [data]);
  const totalCuentas = useMemo(()=> data?.totales?.cuentas  || 0, [data]);
  const totalGeneral = useMemo(()=> data?.totales?.general  || 0, [data]);

  // Resumen por banco (cuentas)
  const resumenBancos = useMemo(() => {
    const map = new Map();
    (data?.cuentas || []).forEach(c => {
      const key = c.banco || "-";
      map.set(key, (map.get(key) || 0) + Number(c.saldoActual || 0));
    });
    return Array.from(map.entries()).map(([banco, total]) => ({ banco, total }))
      .sort((a,b)=> b.total - a.total);
  }, [data]);

  // Resumen por caja (simple suma por descripción)
  const resumenCajas = useMemo(() => {
    const map = new Map();
    (data?.cajas || []).forEach(c => {
      const key = c.descripcion || `Caja ${c.codcaj}`;
      map.set(key, (map.get(key) || 0) + Number(c.saldoActual || 0));
    });
    return Array.from(map.entries()).map(([caja, total]) => ({ caja, total }))
      .sort((a,b)=> b.total - a.total);
  }, [data]);

  // Abre modal y trae detalle de EMITIDOS
  const openDetalle = async (tipo /* 'cheque' | 'icheq' */) => {
    setDetTitulo(tipo === "icheq" ? "Detalle iCheq emitidos" : "Detalle cheques emitidos");
    const params = { tipo };
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    const resp = await getEmitidosTesoreria(params);
    if (!resp?.ok) return alert(resp?.msg || "No se pudo obtener el detalle.");
    setDetRows(resp.items || []);
    setDetTotal(resp.total || 0);
    setShowDet(true);
  };

  // Abre modal y trae detalle de RECIBIDOS
  const openDetalleRecibidos = async (estado = "EN_CARTERA") => {
    setDetRecTitulo(`Valores recibidos — ${estado.replace("_"," ")}`);
    const params = { estado };
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    const resp = await getRecibidosTesoreria(params);
    if (!resp?.ok) return alert(resp?.msg || "No se pudo obtener el detalle.");
    setDetRecRows(resp.items || []); setDetRecTotal(resp.total || 0);
    setShowDetRec(true);
  };

  return (
    <Container fluid className="py-3">
      <Row className="align-items-end g-2">
        <Col md={2}>
          <Form.Label>Desde</Form.Label>
          <Form.Control type="date" value={desde} onChange={e=>setDesde(e.target.value)} />
        </Col>
        <Col md={2}>
          <Form.Label>Hasta</Form.Label>
          <Form.Control type="date" value={hasta} onChange={e=>setHasta(e.target.value)} />
        </Col>
        <Col md="auto" className="mt-4">
          <Button className="me-2" onClick={cargar}>Actualizar</Button>
          <ButtonGroup>
            <Button variant="outline-secondary" onClick={()=>setPreset("hoy")}>Hoy</Button>
            <Button variant="outline-secondary" onClick={()=>setPreset("semana")}>Semana</Button>
            <Button variant="outline-secondary" onClick={()=>setPreset("mes")}>Mes</Button>
            <Button variant="outline-secondary" onClick={()=>setPreset("mes_anterior")}>Mes ant.</Button>
            <Button variant="outline-secondary" onClick={()=>setPreset("anio")}>Año</Button>
          </ButtonGroup>
        </Col>
      </Row>

      {/* Totales */}
      <Row className="mt-3 g-3">
        <Col md={3}>
          <Card><Card.Body><div className="text-muted">Efectivo (Cajas)</div><div className="fs-4">${$(totalCajas)}</div></Card.Body></Card>
        </Col>
        <Col md={3}>
          <Card><Card.Body><div className="text-muted">Cuentas Bancarias</div><div className="fs-4">${$(totalCuentas)}</div></Card.Body></Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body>
              <div className="text-muted">
                Cheques emitidos{" "}
                <Badge bg="secondary" style={{ cursor: "pointer" }} onClick={() => openDetalle("cheque")}>info</Badge>
              </div>
              <div className="fs-5">${$(data?.cheques?.emitidos || 0)}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body>
              <div className="text-muted">
                iCheq emitidos{" "}
                <Badge bg="secondary" style={{ cursor: "pointer" }} onClick={() => openDetalle("icheq")}>info</Badge>
              </div>
              <div className="fs-5">${$(data?.icheq?.emitidos || 0)}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Valores recibidos (cartera) */}
      <Row className="mt-3">
        <Col md={12}>
          <Card>
            <Card.Body>
              <div className="text-muted d-flex align-items-center justify-content-between flex-wrap">
                <span>Valores recibidos (cartera)</span>
                <div className="d-flex gap-2">
                  <Badge bg="secondary" style={{ cursor: "pointer" }} onClick={() => openDetalleRecibidos("EN_CARTERA")}>
                    en cartera
                  </Badge>
                  <Badge bg="secondary" style={{ cursor: "pointer" }} onClick={() => openDetalleRecibidos("DEPOSITADO")}>
                    depositado
                  </Badge>
                  <Badge bg="secondary" style={{ cursor: "pointer" }} onClick={() => openDetalleRecibidos("ACREDITADO")}>
                    acreditado
                  </Badge>
                </div>
              </div>
              <div className="fs-6 mt-2">
                En cartera: <b>${$(data?.carteraRecibidos?.enCartera || 0)}</b> ·
                <span className="ms-2">Depositado: <b>${$(data?.carteraRecibidos?.depositado || 0)}</b></span> ·
                <span className="ms-2">Acreditado: <b>${$(data?.carteraRecibidos?.acreditado || 0)}</b></span>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Cajas */}
      <Row className="mt-4">
        <Col md={8}>
          <h5>Cajas</h5>
          <Table bordered hover size="sm" style={tbl}>
            <thead>
              <tr>
                <th style={cell}>Código</th>
                <th style={cell}>Descripción</th>
                <th style={{ ...cell, ...right }}>Saldo</th>
              </tr>
            </thead>
            <tbody>
              {(data?.cajas || []).map(c => (
                <tr key={c._id}>
                  <td style={cell}>{c.codcaj}</td>
                  <td style={cell}>{c.descripcion}</td>
                  <td style={{ ...cell, ...right }}>${$(c.saldoActual)}</td>
                </tr>
              ))}
              <tr className="table-light">
                <td style={cell} colSpan={2}><b>Total Cajas</b></td>
                <td style={{ ...cell, ...right }}><b>${$(totalCajas)}</b></td>
              </tr>
            </tbody>
          </Table>
        </Col>
        <Col md={4}>
          <h6 className="mt-4 mt-md-0">Resumen por caja</h6>
          <Table bordered size="sm" style={{ ...tbl, fontSize: "0.95rem" }}>
            <thead>
              <tr>
                <th style={cell}>Caja</th>
                <th style={{ ...cell, ...right }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {resumenCajas.map((r,i)=>(
                <tr key={i}>
                  <td style={cell}>{r.caja}</td>
                  <td style={{ ...cell, ...right }}>${$(r.total)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>

      {/* Cuentas Bancarias */}
      <Row className="mt-4">
        <Col md={8}>
          <h5>Cuentas bancarias</h5>
          <Table bordered hover size="sm" style={tbl}>
            <thead>
              <tr>
                <th style={cell}>Banco</th>
                <th style={cell}>Alias / N°</th>
                <th style={cell}>Tipo</th>
                <th style={cell}>Moneda</th>
                <th style={{ ...cell, ...right }}>Saldo</th>
              </tr>
            </thead>
            <tbody>
              {(data?.cuentas || []).map(c => (
                <tr key={c._id}>
                  <td style={cell}>{c.banco || "-"}</td>
                  <td style={cell}>{c.alias || c.numero || c.cbu || "-"}</td>
                  <td style={cell}>{c.tipo || "-"}</td>
                  <td style={cell}>{c.moneda || "ARS"}</td>
                  <td style={{ ...cell, ...right }}>${$(c.saldoActual)}</td>
                </tr>
              ))}
              <tr className="table-light">
                <td style={cell} colSpan={4}><b>Total Cuentas</b></td>
                <td style={{ ...cell, ...right }}><b>${$(totalCuentas)}</b></td>
              </tr>
            </tbody>
          </Table>
        </Col>
        <Col md={4}>
          <h6 className="mt-4 mt-md-0">Resumen por banco</h6>
          <Table bordered size="sm" style={{ ...tbl, fontSize: "0.95rem" }}>
            <thead>
              <tr>
                <th style={cell}>Banco</th>
                <th style={{ ...cell, ...right }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {resumenBancos.map((r,i)=>(
                <tr key={i}>
                  <td style={cell}>{r.banco}</td>
                  <td style={{ ...cell, ...right }}>${$(r.total)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>

      {/* Total General */}
      <Row className="mt-4">
        <Col md={4}>
          <Card><Card.Body><div className="text-muted">Total disponible (Cajas + Cuentas)</div><div className="fs-3">${$(totalGeneral)}</div></Card.Body></Card>
        </Col>
      </Row>

      {/* MODAL Detalle EMITIDOS */}
      <Modal show={showDet} onHide={() => setShowDet(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{detTitulo}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table bordered hover size="sm" style={tbl}>
            <thead>
              <tr>
                <th style={cell}>Fecha</th>
                <th style={cell}>N° OP</th>
                <th style={cell}>Tercero</th>
                <th style={cell}>Medio</th>
                <th style={cell}>Ref / N°</th>
                <th style={{ ...cell, ...right }}>Importe</th>
              </tr>
            </thead>
            <tbody>
              {detRows.map((r, i) => (
                <tr key={i}>
                  <td style={cell}>{dateStr(r.fecha)}</td>
                  <td style={cell}>{r.numeroOP}</td>
                  <td style={cell}>{r.tercero}</td>
                  <td style={cell}>{r.medio}</td>
                  <td style={cell}>{r.referencia || r.nroCheque || "-"}</td>
                  <td style={{ ...cell, ...right }}>${$(r.importe)}</td>
                </tr>
              ))}
              <tr className="table-light">
                <td style={cell} colSpan={5}><b>Total</b></td>
                <td style={{ ...cell, ...right }}><b>${$(detTotal)}</b></td>
              </tr>
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={exportEmitidosCSV} disabled={!detRows.length}>
            Exportar CSV
          </Button>
          <Button variant="outline-success" onClick={exportEmitidosPDF} disabled={!detRows.length}>
            Exportar PDF
          </Button>
          <Button variant="secondary" onClick={() => setShowDet(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL Detalle RECIBIDOS */}
      <Modal show={showDetRec} onHide={() => setShowDetRec(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{detRecTitulo}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table bordered hover size="sm" style={tbl}>
            <thead>
              <tr>
                <th style={cell}>Fecha recibida</th>
                <th style={cell}>Cliente</th>
                <th style={cell}>Tipo</th>
                <th style={cell}>Ref / N°</th>
                <th style={{ ...cell, ...right }}>Importe</th>
              </tr>
            </thead>
            <tbody>
              {detRecRows.map((r, i) => (
                <tr key={i}>
                  <td style={cell}>{dateStr(r.recibidoEn)}</td>
                  <td style={cell}>{r?.clienteId?.razonsocial || r?.clienteId?.nombre || "-"}</td>
                  <td style={cell}>{r.tipo}</td>
                  <td style={cell}>{r.idICheq || r.nroCheque || "-"}</td>
                  <td style={{ ...cell, ...right }}>${$(r.importe)}</td>
                </tr>
              ))}
              <tr className="table-light">
                <td style={cell} colSpan={4}><b>Total</b></td>
                <td style={{ ...cell, ...right }}><b>${$(detRecTotal)}</b></td>
              </tr>
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={exportRecibidosCSV} disabled={!detRecRows.length}>
            Exportar CSV
          </Button>
          <Button variant="outline-success" onClick={exportRecibidosPDF} disabled={!detRecRows.length}>
            Exportar PDF
          </Button>
          <Button variant="secondary" onClick={() => setShowDetRec(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
